/* eslint-disable @reatom/unit-naming-rule */
import { action } from '@reatom/framework'
import { BigNumber } from 'bignumber.js'

import { Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency/model'
import { quoteSubscriptionRegistry } from '@root/features/quote/atoms/reatom-quote-registry'
import { nativePriceByNetwork } from '@root/features/realtime/atoms'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'

import { processBuySwapAction } from '../../swap'
import {
  serverSwapText,
  swapFromServerWalletAction,
} from '../../swap/atoms/server-swap'
import { tradeSettingsStateAtom } from '../../trade-settings'
import { quickSwapAmountBySectionAtom } from '../../trade-settings/atoms'
import { tradingWalletAtom } from '../../trading'

import type { PairModel } from '@root/entities/pair'
import type {
  TradeSettings,
  TradeSettingsQuick,
} from '@root/entities/trade-settings/model'
import type { WalletModel } from '@root/entities/wallet/atoms'

type QuickBuyArgs = {
  pair: PairModel
  section: TradeSettingsQuick.QuickSwapSection
}

export const reatomQuickBuy = ({ pair, section }: QuickBuyArgs) => {
  type ProcessArgs = {
    amountNative: BigNumber
    wallet: WalletModel
    settings: TradeSettings.TradeSettings
  }

  const processNativeQuickBuyAction = action(async (ctx, args: ProcessArgs) => {
    const { amountNative, wallet, settings } = args

    const baseCurrency = CurrencyModel.isNativeOrWrapped(pair.network)(
      pair.base,
    )
      ? pair.base
      : CurrencyModel.Sol

    return processBuySwapAction(ctx, {
      amount: amountNative,
      pair,
      wallet: wallet,
      settings: settings,
      quote: undefined,
      baseCurrencyOverride: baseCurrency,
    })
  })

  const processStableQuickBuyAction = action(async (ctx, args: ProcessArgs) => {
    const { amountNative, wallet, settings } = args
    const baseAsset = ctx.get(wallet.assetsByCurrency)[pair.base.address]
    const baseBalance = baseAsset ? ctx.get(baseAsset.balance) : BigNumber(0)

    const amountStable = (() => {
      const usdPrice = ctx.get(nativePriceByNetwork[pair.network])
      const amountUsd = amountNative.multipliedBy(usdPrice)
      return amountUsd
    })()

    if (baseBalance.lt(amountStable)) {
      // if not sufficient usd stable, try native
      return processNativeQuickBuyAction(ctx, args)
    }

    const quote = quoteSubscriptionRegistry.get(ctx, {
      pair: pair,
      type: 'buy',
      amount: amountNative,
    })

    return processBuySwapAction(ctx, {
      amount: amountStable,
      pair,
      wallet: wallet,
      settings: settings,
      quote,
    })
  })

  const quickBuyAction = action(async (ctx) => {
    const settings = ctx.get(tradeSettingsStateAtom).value
    const wallet = ctx.get(tradingWalletAtom)
    const amountNative = ctx.get(quickSwapAmountBySectionAtom)?.[section].buy

    if (!wallet || !settings || !amountNative || amountNative.isZero()) {
      return Result.fail(Error.UnknownError.fromUnknown('invalid input'))
    }

    /*
     * A wallet whose key we keep is traded on our side.
     *
     * Everything below this signs on the machine it runs on, through a store
     * only the desktop build has — which is why a browser's buy button has
     * never bought anything and offered a download instead. For a wallet made
     * on the server there is a key to sign with, so the trade goes there.
     */
    if (ctx.get(wallet.custody) === 'server') {
      const result = await ctx.schedule(() =>
        swapFromServerWalletAction(ctx, {
          walletId: wallet.id,
          side: 'buy',
          mint: pair.quote.address,
          amount: amountNative.toFixed(),
          slippagePercent: settings.slippage,
        }),
      )

      return result.pipe(
        /* The signature is not what the button needs — only that it went
           through — and saying so keeps this the same shape as the path
           below it. */
        Result.map(() => undefined),
        Result.mapError((problem) =>
          Error.UnknownError.fromUnknown(serverSwapText[problem]),
        ),
      )
    }

    if (CurrencyModel.isUsdStableCurrency(pair.base)) {
      return processStableQuickBuyAction(ctx, {
        amountNative,
        wallet,
        settings,
      })
    }

    return processNativeQuickBuyAction(ctx, {
      amountNative,
      wallet,
      settings,
    })
  }, 'quickBuyAction')

  const requestState = reatomRequest({
    execAction: quickBuyAction,
    name: 'quickBuy',
  })

  return requestState
}
